import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { DashboardTreeService, WellSearchResult } from '../../services/dashboard-tree.service';
import { LookupService } from '../../services/lookup.service';
import { WellSearchDialogResult } from '../well-search-dialog/well-search-dialog.component';

export interface CreateCustomWellDialogData {
    wellSearchResult?: WellSearchResult;
}

@Component({
    selector: 'app-create-custom-well-dialog',
    standalone: true,
    imports: [MaterialModule, FormsModule, SpinnerComponent],
    templateUrl: './create-custom-well-dialog.component.html',
    styleUrls: ['./create-custom-well-dialog.component.css'],
})
export class CreateCustomWellDialogComponent implements OnInit {
    selectedBusinessUnitCode = '';
    wellLocation = '';
    wellName = '';
    isSaving = false;
    isLoadingBUs = true;
    businessUnits: { code: string; name: string }[] = [];
    isSearchResultMode = false;
    wellSearchResult: WellSearchResult | null = null;

    constructor(
        private dialogRef: MatDialogRef<CreateCustomWellDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CreateCustomWellDialogData | null,
        private dashboardTreeService: DashboardTreeService,
        private lookupService: LookupService,
    ) { }

    ngOnInit(): void {
        if (this.data?.wellSearchResult) {
            this.isSearchResultMode = true;
            this.wellSearchResult = this.data.wellSearchResult;
        }
        this.loadBusinessUnits();
    }

    private loadBusinessUnits(): void {
        this.lookupService.getBusinessUnits().subscribe({
            next: (units) => {
                this.businessUnits = units.map(u => ({ code: u.sbuCode, name: u.sbuName }));
                if (this.businessUnits.length > 0) {
                    this.selectedBusinessUnitCode = this.businessUnits[0].code;
                }
                this.isLoadingBUs = false;
            },
            error: (err) => {
                console.error('Failed to load business units', err);
                this.isLoadingBUs = false;
            },
        });
    }

    get selectedBusinessUnitName(): string {
        return this.businessUnits.find(b => b.code === this.selectedBusinessUnitCode)?.name ?? '';
    }

    create(): void {
        if (!this.isSearchResultMode && !this.wellName.trim()) return;
        this.isSaving = true;

        const request = this.isSearchResultMode && this.wellSearchResult
            ? {
                sbuCode: this.selectedBusinessUnitCode,
                wellName: this.wellSearchResult.wellName,
                wellLocation: this.wellSearchResult.wellLocation,
                api12: this.wellSearchResult.api12,
                chevNo: this.wellSearchResult.chevNo,
                fieldName: this.wellSearchResult.fieldName,
                leaseName: this.wellSearchResult.leaseName,
                country: this.wellSearchResult.country,
                operatorName: this.wellSearchResult.operatorName,
            }
            : {
                sbuCode: this.selectedBusinessUnitCode,
                wellLocation: this.wellLocation,
                wellName: this.wellName.trim(),
            };

        this.dashboardTreeService.createCustomWell(request).subscribe({
            next: (result) => {
                this.isSaving = false;
                this.dialogRef.close({
                    wellId: result.wellId,
                    wellName: result.wellName,
                    businessUnitCode: this.selectedBusinessUnitCode,
                    businessUnitName: this.selectedBusinessUnitName,
                    wellLocation: this.wellSearchResult?.fieldName,
                } as WellSearchDialogResult);
            },
            error: (err) => {
                console.error('Failed to create custom well', err);
                this.isSaving = false;
            },
        });
    }

    cancel(): void {
        this.dialogRef.close(null);
    }
}
